import React, { ComponentProps, useRef, useState } from 'react';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { Icons } from '../../../components/atoms/SVG';
import { asFetchableView, useRefreshView } from '../../../_lib/ViewFetcher';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { DraggableCalendarListItem } from '../partials/CalendarListItem';
import { SortableContentList } from '../../../_lib/components/SortableContentList';
import { useArrayEffect } from '../../../_lib/components/SortableContentList/SortableContentList';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { CalendarDeleteForm } from '../partials/CalendarDeleteForm';
import { CalendarEditableElement, CalendarForm } from '../CalendarTypes';
import { CalendarAddOrUpdateForm } from '../partials/CalendarAddOrUpdateForm';
import { SonForm } from '../../../_lib/Form2';

type CalendarEditableListProps = {
  items: CalendarEditableElement[];
  create: CalendarForm;
  updateOrder?: CalendarForm;
};

export function CalendarEdit({ items, create, updateOrder }: CalendarEditableListProps) {
  const { t } = useSonTranslation('Calendar');
  const ref = useRef<HTMLButtonElement>(null);

  const { delayedRefreshView } = useRefreshView();

  const [activeForm, setActiveForm] = useState<{
    type: 'add' | 'delete';
    form: CalendarForm | SonForm<any>;
  } | null>(null);

  /**
   * After successfully changes, modal should be closed
   * */
  useArrayEffect(() => setActiveForm(null), items);

  const closeModal = () => setActiveForm(null);
  const openAddForm = () => setActiveForm({ type: 'add', form: create });
  const openDeleteForm = (deleteForm: SonForm<any>) => setActiveForm({ type: 'delete', form: deleteForm });

  const getActiveModalDialog = () => {
    switch (activeForm?.type) {
      case 'delete':
        return (
          <ModalDialog closeModal={closeModal} headline={t('removeCalendar')} focusOnReturn={ref} initialFocusIndex={1}>
            <CalendarDeleteForm form={activeForm.form as SonForm<any>} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      case 'add':
        return (
          <ModalDialog closeModal={closeModal} headline={t('addCalendar')} initialFocusIndex={1}>
            <CalendarAddOrUpdateForm form={activeForm.form as CalendarForm} refresh={delayedRefreshView} />
          </ModalDialog>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <BlockBar
        heading={t('addCalendar')}
        hierarchy={3}
        modifier={items?.length > 0 ? ['border-bottom', 'border-top'] : 'border-top'}
      >
        <BlockBarButton ref={ref} onClick={openAddForm} text={t('addCalendar')} svgSpriteId={Icons.ADD_PLUS} />
        <Heading hierarchy={4} className="invisible">
          {t('existingCalendars')}:
        </Heading>
      </BlockBar>
      <SortableContentList items={items || []} form={updateOrder}>
        {(item: CalendarEditableElement) => (
          <DraggableCalendarListItem key={item.id} {...item} openDeleteForm={openDeleteForm} />
        )}
      </SortableContentList>
      {getActiveModalDialog()}
    </>
  );
}

export type CalendarEditView = ComponentProps<typeof CalendarEditView>;
export const CalendarEditView = asFetchableView(CalendarEdit);
